import { Link } from "react-router-dom";
import { Shield, Lock, ServerCog, Fingerprint, Globe, ShieldCheck, Mail, ArrowLeft, Download, Users, Building2 } from "lucide-react";

/**
 * Public trust page — /trust.
 * Marketing-grade summary of the security & privacy posture. Kept in sync
 * with the platform's actual controls (see security audit iteration 10).
 */
export default function Trust() {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="border-b border-[#E2E8F0] bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="trust-header-home">
            <div className="w-7 h-7 rounded-md bg-[#0A2342] flex items-center justify-center">
              <span className="text-[#4FD1C5] font-heading font-bold text-xs">D</span>
            </div>
            <span className="font-heading font-bold text-[14px] text-[#0A2342]">Dive Media Strategist™</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-[13.5px] text-[#1D4E89] hover:text-[#0A2342]"><ArrowLeft className="w-3.5 h-3.5" /> Home</Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A2342] to-[#1D4E89]" />
        <div className="absolute inset-0 opacity-[0.15] mix-blend-overlay bg-cover bg-center" style={{ backgroundImage: "url('https://images.pexels.com/photos/3721304/pexels-photo-3721304.jpeg?auto=compress&cs=tinysrgb&h=650&w=940')" }} />
        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-24">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-[#4FD1C5] text-[10.5px] tracking-[0.28em] uppercase font-bold" data-testid="trust-badge">
            <ShieldCheck className="w-3.5 h-3.5" /> Trust & Security
          </div>
          <h1 className="mt-6 font-heading font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight max-w-3xl">
            Built to earn your dive centre&apos;s trust.
          </h1>
          <p className="mt-6 text-white/80 text-[16px] sm:text-[17px] leading-relaxed max-w-2xl">
            Dive shops, PADI instructors and marine-conservation brands ship their entire content strategy through DMS. Here&apos;s exactly how we protect that data, your paying students, and your reputation.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-6 text-white/70 text-[13px]">
            <div className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#4FD1C5]" /> Last independently audited: Feb 2026</div>
            <div className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#4FD1C5]" /> 100% of critical findings resolved</div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-24">
        <div className="mb-12">
          <div className="dms-label">The pillars</div>
          <h2 className="mt-2 font-heading font-bold text-3xl sm:text-4xl text-[#0A2342] tracking-tight max-w-xl leading-tight">Six things every dive centre should ask their tools.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <Pillar icon={Lock} title="Encrypted in transit and at rest" data-tid="pillar-encryption">
            All traffic runs over TLS 1.2+. Your Dive Brain, vault items and uploads are stored in a MongoDB cluster with disk-level encryption. Nothing lives on a laptop or a spreadsheet.
          </Pillar>
          <Pillar icon={ShieldCheck} title="Payments never touch our servers" data-tid="pillar-payments">
            All checkouts and card details are handled by Stripe (PCI DSS Level 1). We only receive an anonymised customer id and a subscription state — no card numbers, ever.
          </Pillar>
          <Pillar icon={Shield} title="Bot &amp; abuse protection" data-tid="pillar-bots">
            Google reCAPTCHA v3 silently scores every registration and login. Score-based blocking + concurrency-safe credit metering means bots can&apos;t burn your account credits.
          </Pillar>
          <Pillar icon={Fingerprint} title="Strict per-workspace data isolation" data-tid="pillar-isolation">
            Every Dive Brain, vault item, campaign and uploaded file is scoped by user + workspace. There is no admin backdoor into your generated content. Multi-workspace users see only what they&apos;ve explicitly switched into.
          </Pillar>
          <Pillar icon={ServerCog} title="Upload safety &amp; SSRF protection" data-tid="pillar-uploads">
            Uploads are content-sniffed and locked to a strict allowlist (images, PDF, DOCX, TXT). URLs you paste into onboarding are validated against private IP ranges and cloud-metadata endpoints before any request goes out — so a hostile URL can never pivot inside our network.
          </Pillar>
          <Pillar icon={Globe} title="Session security &amp; auth" data-tid="pillar-auth">
            Session tokens use cryptographically-random 256-bit values, invalidated on logout. Passwords are bcrypt-hashed with a modern work factor. Email verification links expire in 48 hours and are single-use.
          </Pillar>
        </div>
      </section>

      {/* Your data */}
      <section className="bg-white border-y border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10 lg:gap-16">
            <div>
              <div className="dms-label">Your data</div>
              <h2 className="mt-2 font-heading font-bold text-3xl sm:text-4xl text-[#0A2342] tracking-tight leading-tight">You own it. You can leave with it.</h2>
            </div>
            <div className="space-y-8 text-[#1F2937]">
              <FactRow icon={Download} title="Portable">
                Every vault item (reel, carousel, hook, blog, email, product page, campaign) has TXT and DOCX export from the Vault detail page. Your brand voice, your files.
              </FactRow>
              <FactRow icon={Users} title="Your team, your call">
                We don&apos;t sell customer lists. We don&apos;t share brand data across accounts. Your Dive Brain is never used to train models — it&apos;s the input to a stateless API call, nothing more.
              </FactRow>
              <FactRow icon={Building2} title="Deletion means deletion">
                Account deletion soft-deletes all workspaces, dive brains, vault items and campaigns immediately. On request we hard-delete within 30 days, in line with GDPR Article 17.
              </FactRow>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-20">
        <div className="dms-label">Compliance</div>
        <h2 className="mt-2 font-heading font-bold text-3xl sm:text-4xl text-[#0A2342] tracking-tight leading-tight max-w-2xl">Where we stand and what&apos;s on the roadmap.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          <ComplianceCard status="live" title="GDPR (EU)" data-tid="comp-gdpr">
            Data-subject rights (access, rectification, erasure, portability) are honoured via written request to hello@divemediastrategist.com within 30 days. Data Processing Addendum available on request for dive-centre accounts.
          </ComplianceCard>
          <ComplianceCard status="live" title="Stripe PCI DSS Level 1" data-tid="comp-pci">
            All payment processing is delegated to Stripe. No cardholder data enters our systems.
          </ComplianceCard>
          <ComplianceCard status="roadmap" title="SOC 2 Type I" data-tid="comp-soc2">
            Preparation begins Q4 2026 alongside our first enterprise dive-centre chain contracts.
          </ComplianceCard>
          <ComplianceCard status="roadmap" title="ISO 27001" data-tid="comp-iso">
            Under evaluation. Priority if a customer&apos;s parent group requires it.
          </ComplianceCard>
        </div>
      </section>

      {/* Contact */}
      <section className="bg-[#0A2342]">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 py-16 text-center">
          <Mail className="w-8 h-8 text-[#4FD1C5] mx-auto" />
          <h2 className="mt-4 font-heading font-bold text-white text-2xl sm:text-3xl tracking-tight">Found something? Tell us.</h2>
          <p className="mt-3 text-white/75 text-[15px] max-w-lg mx-auto leading-relaxed">
            We take responsible disclosure seriously. Email details and a proof of concept — we&apos;ll acknowledge within 48 hours.
          </p>
          <a href="mailto:hello@divemediastrategist.com" data-testid="trust-security-email" className="mt-6 inline-flex items-center gap-2 bg-[#4FD1C5] text-[#0A2342] font-heading font-bold text-[14px] px-6 py-3 rounded-md hover:bg-white transition-colors">
            hello@divemediastrategist.com
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E2E8F0] py-8 text-center text-[11px] sm:text-xs tracking-[0.18em] text-[#64748B] uppercase bg-white">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
          <Link to="/legal/terms" className="hover:text-[#0A2342]">Terms</Link>
          <Link to="/legal/privacy" className="hover:text-[#0A2342]">Privacy</Link>
          <Link to="/legal/disclaimer" className="hover:text-[#0A2342]">Disclaimer</Link>
          <Link to="/trust" className="text-[#0A2342] font-bold">Trust</Link>
        </div>
        <div className="mt-4 text-[10px] tracking-[0.22em]">© {new Date().getFullYear()} Dive Media Strategist™</div>
      </footer>
    </div>
  );
}

function Pillar({ icon: Icon, title, children, "data-tid": tid }) {
  return (
    <div className="dms-card p-6 hover:-translate-y-0.5 hover:shadow-lg hover:border-[#4FD1C5]/60 transition-all group" data-testid={tid}>
      <div className="w-11 h-11 rounded-lg bg-[#0A2342] text-[#4FD1C5] flex items-center justify-center mb-4 group-hover:scale-[1.05] transition-transform">
        <Icon className="w-5 h-5" />
      </div>
      <div className="font-heading font-bold text-[17px] text-[#0A2342] leading-snug mb-2">{title}</div>
      <div className="text-[13.5px] text-[#475569] leading-relaxed">{children}</div>
    </div>
  );
}

function FactRow({ icon: Icon, title, children }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-md bg-[#F1F5F9] text-[#0A2342] flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></div>
      <div>
        <div className="font-heading font-bold text-[16px] text-[#0A2342]">{title}</div>
        <div className="text-[14px] text-[#475569] leading-relaxed mt-1">{children}</div>
      </div>
    </div>
  );
}

function ComplianceCard({ status, title, children, "data-tid": tid }) {
  const isLive = status === "live";
  return (
    <div className="dms-card p-6" data-testid={tid}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`dms-badge ${isLive ? "bg-[#ECFDF5] text-[#065F46] border-[#10B981]/30" : "bg-[#F1F5F9] text-[#64748B] border-[#E2E8F0]"}`}>
          {isLive ? "Live" : "On roadmap"}
        </span>
      </div>
      <div className="font-heading font-bold text-[17px] text-[#0A2342] leading-snug">{title}</div>
      <div className="text-[13.5px] text-[#475569] leading-relaxed mt-2">{children}</div>
    </div>
  );
}
